/**
 * Der BWA Monatsbericht als Briefing: oben Kennzahlen, Monatsverlauf und
 * Kosten, darunter jeder Punkt als eine Zeile. Ein Klick oeffnet den Punkt
 * auf eigener Seite; zurueck geht es an dieselbe Stelle der Uebersicht.
 *
 * Alle Zahlen rechnet der Server aus der BWA (berichtansicht.ts), die KI hat
 * nur Text, Prioritaet und Messpunkt beigetragen. Gibt es keine Ansicht
 * (aeltere oder von Hand bearbeitete Analyse), zeigt das Feld den Text.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { euro, euroGanz, mitVorzeichen, prozent } from './format'
import { Punktansicht, stand } from './Punktansicht'
import { Legende, Saeulen } from './Saeulen'
import { gruppeName, type Diagramm, type Gruppe, type Kennzahlen, type Monatsbericht, type Punkt, type Wertpaar, type Zeitraum } from './typen'

/** Unterschiede darunter gelten als gleich: in Prozentpunkten. */
const GLEICH_PUNKTE = 0.5

const DIAGRAMM_GRUPPE: Record<Diagramm['gruppe'], string> = { ergebnis: 'Ergebnis', kosten: 'Kosten', liquiditaet: 'Liquidität' }

/** Was eine Saeule im gewaehlten Zeitraum zeigt, fuer Ueberschrift und Legende. */
function saeulenSinn(d: Diagramm, z: Zeitraum, quartal: boolean): { titel: string; wert: string } {
  if (d.art === 'bestand') return { titel: 'Stand am Monatsende', wert: 'Stand am Monatsende' }
  if (z === 'lfd_jahr') return { titel: 'aufgelaufen seit Januar', wert: 'aufgelaufen seit Januar' }
  if (z === 'zwoelf_monate') return { titel: 'jeweils die letzten 12 Monate', wert: '12 Monate bis dahin' }
  return quartal ? { titel: 'je Quartal', wert: 'Quartalswert' } : { titel: 'je Monat', wert: 'Monatswert' }
}

/**
 * Welches Diagramm: ein eigenes aufklappendes Menue statt <select>, das der
 * Browser auf jedem Rechner anders zeichnet. Gruppiert nach Ergebnis, Kosten,
 * Liquiditaet; Escape und ein Klick daneben schliessen.
 */
function Diagrammwahl({ liste, wahl, setzen }: { liste: Diagramm[]; wahl: Diagramm; setzen: (schluessel: string) => void }) {
  const [offen, setOffen] = useState(false)
  const huelle = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!offen) return
    const klick = (e: PointerEvent) => {
      if (!huelle.current?.contains(e.target as Node)) setOffen(false)
    }
    const taste = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        setOffen(false)
      }
    }
    window.addEventListener('pointerdown', klick)
    window.addEventListener('keydown', taste, true)
    return () => {
      window.removeEventListener('pointerdown', klick)
      window.removeEventListener('keydown', taste, true)
    }
  }, [offen])
  const gruppen = (['ergebnis', 'kosten', 'liquiditaet'] as const).map((g) => ({ g, eintraege: liste.filter((d) => d.gruppe === g) })).filter((x) => x.eintraege.length)
  return (
    <div className="diagrammwahl" ref={huelle}>
      <button type="button" className="diagrammwahl-knopf" aria-haspopup="true" aria-expanded={offen} onClick={() => setOffen(!offen)}>
        {wahl.name} <span aria-hidden="true">▾</span>
      </button>
      {offen && (
        <div className="diagrammwahl-liste" role="menu">
          {gruppen.map(({ g, eintraege }) => (
            <div key={g} className="diagrammwahl-gruppe">
              <div className="diagrammwahl-gruppenname">{DIAGRAMM_GRUPPE[g]}</div>
              {eintraege.map((d) => (
                <button
                  key={d.schluessel}
                  type="button"
                  role="menuitemradio"
                  aria-checked={d.schluessel === wahl.schluessel}
                  onClick={() => {
                    setzen(d.schluessel)
                    setOffen(false)
                  }}
                >
                  {d.name}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function Umschalter<T extends string>({ werte, wahl, setzen, name }: { werte: readonly { wert: T; name: string }[]; wahl: T; setzen: (w: T) => void; name: string }) {
  return (
    <div className="umschalter" role="group" aria-label={name}>
      {werte.map((w) => (
        <button key={w.wert} type="button" aria-pressed={w.wert === wahl} onClick={() => setzen(w.wert)}>
          {w.name}
        </button>
      ))}
    </div>
  )
}

/** Veraenderung zum Vorjahr; hoeher ist bei allen drei Kennzahlen besser. */
function Vergleich({ paar, vorjahr }: { paar: Wertpaar; vorjahr: string }) {
  const { jetzt, vorjahr: vj } = paar
  if (jetzt === null || vj === null) return <div className="kachel-vergleich nebensache">{vorjahr}: kein Wert</div>
  const diff = jetzt - vj
  const richtung = diff === 0 ? '' : diff > 0 ? ' gut' : ' schlecht'
  // Prozent nur, wenn das Vorjahr eine sinnvolle Basis ist: positiv und gleiches Vorzeichen.
  const anteil = vj > 0 && jetzt >= 0 ? (diff / vj) * 100 : null
  return (
    <div className="kachel-vergleich">
      <b className={`kachel-delta${richtung}`}>
        {diff >= 0 ? '+' : '−'}
        {euro(Math.abs(diff))}
        {anteil !== null && ` (${mitVorzeichen(anteil)} %)`}
      </b>{' '}
      <span className="nebensache">
        zu {vorjahr}: {euro(vj)}
      </span>
    </div>
  )
}

function Kacheln({ k }: { k: Kennzahlen }) {
  const q = k.rohertragsquote
  return (
    <div className="kacheln">
      <div className="kachel">
        <div className="kachel-name">Umsatz</div>
        <div className="kachel-wert">{euroGanz(k.umsatz.jetzt)}</div>
        <Vergleich paar={k.umsatz} vorjahr={k.vorjahr} />
      </div>
      <div className="kachel">
        <div className="kachel-name">Rohertrag</div>
        <div className="kachel-wert">{euroGanz(k.rohertrag.jetzt)}</div>
        <Vergleich paar={k.rohertrag} vorjahr={k.vorjahr} />
        {q.jetzt !== null && (
          <div className="kachel-fuss nebensache">
            {prozent(q.jetzt)} der Gesamtleistung{q.vorjahr !== null && `, Vorjahr ${prozent(q.vorjahr)}`}
          </div>
        )}
      </div>
      <div className="kachel">
        <div className="kachel-name">Ergebnis vor Steuern</div>
        <div className="kachel-wert">{euroGanz(k.ergebnis.jetzt)}</div>
        <Vergleich paar={k.ergebnis} vorjahr={k.vorjahr} />
      </div>
    </div>
  )
}

/**
 * Kosten: Betrag und Anteil an der Gesamtleistung, jeweils gegen das
 * Vorjahr. Die Quote allein steht schon in der BWA (Rueckmeldung 22.10.2026);
 * neu sind die Veraenderung in Euro und die Markierung, wenn ein Monat
 * deutlich vom Ueblichen abweicht.
 */
function Kostentabelle({ b, zeitraum }: { b: Monatsbericht; zeitraum: Zeitraum }) {
  const { zeilen, hinweis } = b.kosten[zeitraum]
  const k = b.kennzahlen[zeitraum]
  const auffaellig = zeilen.find((z) => z.auffaellig)?.auffaellig
  return (
    <section className="bericht-teil">
      <h4>Kosten · {k.jetzt} gegen {k.vorjahr}</h4>
      {hinweis ? (
        <p className="nebensache">{hinweis}</p>
      ) : (
        <>
          <table className="kostentabelle">
            <thead>
              <tr>
                <th>Kostenart</th>
                <th className="zahl">Betrag</th>
                <th className="zahl kostentabelle-veraenderung">zum Vorjahr</th>
                <th className="zahl">Anteil</th>
                <th className="zahl kostentabelle-veraenderung">zum Vorjahr</th>
              </tr>
            </thead>
            <tbody>
              {zeilen.map((z) => {
                const d = z.quoteVorjahr === null ? null : z.quote - z.quoteVorjahr
                const de = z.betragVorjahr === null ? null : z.betrag - z.betragVorjahr
                return (
                  <tr key={z.schluessel}>
                    <td>
                      {z.name}
                      {z.auffaellig && (
                        <span className="auffaellig" title={`Sonst um ${prozent(z.auffaellig.ueblich)} der Gesamtleistung (Median der ${z.auffaellig.perioden} Perioden davor)`}>
                          {z.quote > z.auffaellig.ueblich ? 'ungewöhnlich hoch' : 'ungewöhnlich niedrig'}
                        </span>
                      )}
                    </td>
                    <td className="zahl">{euro(z.betrag)}</td>
                    <td className="zahl kostentabelle-veraenderung">
                      {/* Mehr Kosten ist schlechter. */}
                      {de === null ? <span className="nebensache">–</span> : de === 0 ? <span className="nebensache">gleich</span> : <span className={de > 0 ? 'schlecht' : 'gut'}>{de > 0 ? '+' : '−'}{euro(Math.abs(de))}</span>}
                    </td>
                    <td className="zahl">
                      {prozent(z.quote)}
                      {z.auffaellig && <div className="nebensache kostentabelle-ueblich">sonst {prozent(z.auffaellig.ueblich)}</div>}
                    </td>
                    <td className="zahl kostentabelle-veraenderung">
                      {d === null ? (
                        <span className="nebensache">–</span>
                      ) : Math.abs(d) < GLEICH_PUNKTE ? (
                        <span className="nebensache">gleich</span>
                      ) : (
                        // Mehr Kosten je Euro Leistung ist schlechter.
                        <b className={d > 0 ? 'schlecht' : 'gut'}>{mitVorzeichen(d)} Pkt.</b>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          {auffaellig && (
            <p className="nebensache kostentabelle-erklaerung">
              Ungewöhnlich: der Anteil liegt mindestens 4 Prozentpunkte neben dem Üblichen, dem mittleren Anteil der {auffaellig.perioden} Perioden davor.
            </p>
          )}
        </>
      )}
    </section>
  )
}

function Zeile({ p, vorperiode, oeffnen }: { p: Punkt; vorperiode: string; oeffnen: () => void }) {
  return (
    <button type="button" className="punktzeile" onClick={oeffnen}>
      <span className={`prio ${p.prioritaet}`} title={`Priorität ${p.prioritaet}`} />
      <span className="punktzeile-titel">{p.titel}</span>
      <span className={`punktzeile-stand${p.neu ? ' neu' : ''}`}>{stand(p, vorperiode)}</span>
      <span className="punktzeile-pfeil" aria-hidden="true">
        ›
      </span>
    </button>
  )
}

interface Wahl {
  zeitraum: Zeitraum
  setZeitraum: (z: Zeitraum) => void
  diagramm: string
  setDiagramm: (schluessel: string) => void
}

/** Kapitel der Uebersicht; Beratung und Buchhaltung immer, die anderen nur, wenn etwas darin steht. */
const KAPITEL: { gruppe: Gruppe; immer: boolean }[] = [
  { gruppe: 'beratung', immer: true },
  { gruppe: 'buchhaltung', immer: true },
  { gruppe: 'unveraendert', immer: false },
  { gruppe: 'erledigt', immer: false },
]

function Uebersicht({ b, oeffnen, wahl }: { b: Monatsbericht; oeffnen: (i: number) => void; wahl: Wahl }) {
  const { zeitraum, setZeitraum } = wahl
  const k = b.kennzahlen[zeitraum]
  const d = b.diagramme.find((x) => x.schluessel === wahl.diagramm) ?? b.diagramme[0]
  const daten = d?.reihen[zeitraum]
  const letzte = daten?.saeulen.at(-1)
  const quartal = b.art.bericht === 'Quartalsbericht'
  const sinn = d ? saeulenSinn(d, zeitraum, quartal) : null
  return (
    <div className="bericht">
      {/* Ein Zeitraum fuer Kennzahlen, Diagramm und Kosten: so steht er sichtbar ueber allem, was er steuert. */}
      <div className="bericht-zeitraum">
        <span className="nebensache">Zeitraum</span>
        <Umschalter name="Zeitraum" wahl={zeitraum} setzen={setZeitraum} werte={b.zeitraeume.map((z) => ({ wert: z.zeitraum, name: z.name }))} />
      </div>

      <section className="bericht-teil">
        <h4>Kennzahlen · {k.jetzt}</h4>
        <Kacheln k={k} />
        {b.vorjahrHinweis && <p className="nebensache">{b.vorjahrHinweis}</p>}
      </section>

      {d && daten && sinn && (
        <section className="bericht-teil">
          <div className="bericht-teil-kopf">
            <h4>
              {d.name} {sinn.titel}
            </h4>
            <Diagrammwahl liste={b.diagramme} wahl={d} setzen={wahl.setDiagramm} />
          </div>
          <Legende daten={daten} wert={sinn.wert} aktuell={letzte ? `aktuell (${letzte.name} ${letzte.jahr})` : 'aktuell'} />
          <Saeulen daten={daten} beschriftung={`${d.name} ${sinn.titel}, ein Jahr`} />
        </section>
      )}

      <Kostentabelle b={b} zeitraum={zeitraum} />

      {b.kurzfazit && (
        <section className="bericht-teil">
          <h4>Auf einen Blick</h4>
          <p className="bericht-blick">{b.kurzfazit}</p>
          {b.hinweis && <p className="nebensache">{b.hinweis}</p>}
        </section>
      )}

      {KAPITEL.map(({ gruppe, immer }) => {
        const liste = b.punkte.map((p, i) => ({ p, i })).filter(({ p }) => p.gruppe === gruppe)
        if (!liste.length && !immer) return null
        return (
          <section key={gruppe} className="bericht-teil">
            <h4>
              {gruppeName(gruppe, b.art.vorperiode)} <span className="bericht-anzahl">{liste.length}</span>
            </h4>
            {liste.length ? (
              <div className="punktliste">
                {liste.map(({ p, i }) => (
                  <Zeile key={p.id} p={p} vorperiode={b.art.vorperiode} oeffnen={() => oeffnen(i)} />
                ))}
              </div>
            ) : (
              <p className="nebensache">Keine.</p>
            )}
          </section>
        )
      })}
    </div>
  )
}

export function BwaBericht({
  dokumentId,
  stand: geaendert,
  ersatz,
  werkzeuge,
  analysiert,
}: {
  dokumentId: string
  stand: string
  ersatz: ReactNode
  /** PDF und Menue, im Kopf des Berichts statt in einer eigenen Leiste darueber. */
  werkzeuge?: ReactNode
  /** "06.10.2026": wann die Analyse entstand oder zuletzt geaendert wurde. */
  analysiert?: string
}) {
  const abfrage = useQuery({
    queryKey: ['bwa-bericht', dokumentId, geaendert],
    queryFn: () => api.holen<{ bericht: Monatsbericht | null }>(`/api/dokumente/${dokumentId}/bwa/bericht`),
  })
  const [offen, setOffen] = useState<number | null>(null)
  // Die Wahl in der Uebersicht ueberdauert den Blick in einen Punkt.
  const [zeitraum, setZeitraum] = useState<Zeitraum>('periode')
  const [diagramm, setDiagramm] = useState('umsatz')
  const huelle = useRef<HTMLDivElement>(null)
  const gemerkt = useRef(0)

  const scroller = () => huelle.current?.closest('.analysefeld-koerper') ?? null
  function oeffnen(i: number) {
    const s = scroller()
    if (offen === null && s) gemerkt.current = s.scrollTop
    setOffen(i)
  }
  function zurueck() {
    setOffen(null)
  }

  // Beim Wechsel der Ansicht nach oben, beim Zurueck an die gemerkte Stelle.
  useEffect(() => {
    const s = scroller()
    if (s) s.scrollTop = offen === null ? gemerkt.current : 0
  }, [offen])

  useEffect(() => {
    if (offen === null) return
    const taste = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOffen(null)
    }
    window.addEventListener('keydown', taste)
    return () => window.removeEventListener('keydown', taste)
  }, [offen])

  if (abfrage.isPending) return <div className="laedt">Wird geladen …</div>
  const b = abfrage.data?.bericht
  // Ohne Ansicht oder bei einem Fehler: der Text der Analyse, wie bisher.
  if (abfrage.isError || !b) return <>{ersatz}</>

  const punkt = offen === null ? undefined : b.punkte[offen]
  // Wo der Punkt steht: im eigenen Kapitel gezaehlt, nicht ueber alle Kapitel ("3 von 19" war unklar).
  const kapitel = punkt ? b.punkte.filter((p) => p.gruppe === punkt.gruppe) : []
  return (
    <div className="bwabericht" ref={huelle}>
      <div className="bwabericht-kopf">
        <div className="bwabericht-kopf-titel">
          <h3>
            {b.art.bericht} {b.periode}
          </h3>
          <span className="nebensache">
            Daten vom {b.stichtag.split('-').reverse().join('.')}
            {analysiert && ` · analysiert am ${analysiert}`}
          </span>
        </div>
        {werkzeuge && <div className="bwabericht-kopf-werkzeuge">{werkzeuge}</div>}
      </div>
      {punkt && offen !== null ? (
        <Punktansicht
          key={punkt.id}
          punkt={punkt}
          vorperiode={b.art.vorperiode}
          nr={kapitel.indexOf(punkt) + 1}
          anzahl={kapitel.length}
          zurueck={zurueck}
          vorher={offen > 0 ? { titel: b.punkte[offen - 1]!.titel, gehen: () => setOffen(offen - 1) } : null}
          weiter={offen < b.punkte.length - 1 ? { titel: b.punkte[offen + 1]!.titel, gehen: () => setOffen(offen + 1) } : null}
        />
      ) : (
        <Uebersicht b={b} oeffnen={oeffnen} wahl={{ zeitraum, setZeitraum, diagramm, setDiagramm }} />
      )}
      <p className="analysefeld-schluss nebensache">Kanzleiinternes Arbeitspapier</p>
    </div>
  )
}
